import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { FileText, Loader2, RefreshCw, AlertCircle, CreditCard, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegenerateInvoiceModal = ({ open, onClose, operation, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Champs modifiables
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [montantHT, setMontantHT] = useState(0);
  const [montantTVA, setMontantTVA] = useState(0);
  const [tauxTVA, setTauxTVA] = useState(18);
  const [applyTVA, setApplyTVA] = useState(true);
  const [invoiceNotes, setInvoiceNotes] = useState('');

  // Modalités de paiement
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [customPaymentText, setCustomPaymentText] = useState('');
  const selectedPolicy = policies.find(p => p.id === selectedPolicyId);
  const isCustom = selectedPolicy?.payment_type === 'custom';

  // Flag d'initialisation
  const [initialized, setInitialized] = useState(false);

  // ── PM Spec 17/02/2026 — Frais d'immobilisation ──
  // S'affiche UNIQUEMENT si le devis a configuré immobilizationFee (option 2a)
  const immoConfig = useMemo(() => {
    if (!operation) return null;
    const fee = operation.immobilizationFee;
    if (!fee || Number(fee) <= 0) return null;
    return {
      rate: Number(fee),
      franchise: Number(operation.immobilizationFranchise || 0),
      unit: operation.immobilizationFranchiseUnit || 'heures',
    };
  }, [operation]);

  const [immoExpanded, setImmoExpanded] = useState(false);
  const [immoDuration, setImmoDuration] = useState(0);
  const [immoAmountOverride, setImmoAmountOverride] = useState(null); // null = pas d'override
  const [immoNote, setImmoNote] = useState('');

  // Montant immo auto-calculé
  const immoAutoAmount = useMemo(() => {
    if (!immoConfig) return 0;
    const dur = Number(immoDuration) || 0;
    const billable = Math.max(0, dur - immoConfig.franchise);
    const perDay = immoConfig.rate; // toujours par jour
    if (immoConfig.unit === 'heures') {
      return Math.round(billable * (perDay / 24));
    }
    return Math.round(billable * perDay);
  }, [immoConfig, immoDuration]);

  const immoFinalAmount = immoAmountOverride !== null ? Number(immoAmountOverride) : immoAutoAmount;
  const immoBillable = immoConfig ? Math.max(0, Number(immoDuration || 0) - immoConfig.franchise) : 0;

  // Charger les politiques de paiement — uniquement à l'ouverture (pas à chaque re-render de 'operation')
  useEffect(() => {
    if (!open) {
      // Reset complet à la fermeture
      setInitialized(false);
      setCustomPaymentText('');
      setPricingDrift(null);
      return;
    }
    axios.get(`${API}/payment-policies/`, {})
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data.items || []);
        const active = list.filter(p => p.is_active !== false);
        setPolicies(active);
        // Pré-sélectionner uniquement si aucune sélection en cours
        setSelectedPolicyId(prev => {
          if (prev) return prev; // Ne pas écraser un choix déjà fait
          if (operation?.paymentPolicyId) return operation.paymentPolicyId;
          const def = active.find(p => p.is_default);
          return def?.id || active[0]?.id || '';
        });
      })
      .catch(() => {});
  }, [open]); // eslint-disable-line

  // Détection d'un écart entre le tarif client actuel et le montant de la dernière facture
  // (signifie que l'admin a modifié le tarif après une première facturation et s'attend
  //  à voir le nouveau montant pré-rempli)
  const [pricingDrift, setPricingDrift] = useState(null);

  // Initialiser les montants depuis l'opération
  useEffect(() => {
    if (open && operation && !initialized) {
      const lastInvoiceAmount = operation.montantTotalTTC || operation.invoiceAmount || operation.finalPrice || 0;
      const currentTariff = Number(operation.estimatedPrice) || 0;

      // Si le tarif client a été modifié depuis la dernière facture, proposer le nouveau tarif
      const hasDrift = currentTariff > 0 && lastInvoiceAmount > 0 && Math.abs(currentTariff - lastInvoiceAmount) > 1;
      const ttc = hasDrift ? currentTariff : (lastInvoiceAmount || currentTariff);

      const ht = operation.montantTotalHT || 0;
      const tva = operation.montantTVA || 0;
      const shouldApplyTVA = tva > 0 || (operation.montantTotalHT && operation.montantTotalHT !== ttc);
      const finalHT = hasDrift
        ? (shouldApplyTVA ? Math.round(ttc / 1.18) : ttc)
        : (ht > 0 ? ht : (tva > 0 ? Math.round(ttc / 1.18) : ttc));
      const finalTVA = hasDrift
        ? (shouldApplyTVA ? Math.round(finalHT * 0.18) : 0)
        : (tva > 0 ? tva : (ht > 0 ? Math.round(ht * 0.18) : 0));

      setMontantHT(finalHT);
      setMontantTVA(finalTVA);
      setInvoiceAmount(ttc);
      setApplyTVA(shouldApplyTVA);
      setInvoiceNotes(operation.invoiceNotes || '');
      setPricingDrift(hasDrift ? { from: lastInvoiceAmount, to: currentTariff } : null);

      // PM Spec 17/02/2026 — Recharger snapshot immobilisation s'il existe
      const existingImmo = operation.invoiceImmobilization;
      if (existingImmo && Number(existingImmo.amount) > 0) {
        setImmoDuration(Number(existingImmo.duration) || 0);
        setImmoNote(existingImmo.note || '');
        if (existingImmo.overridden) {
          setImmoAmountOverride(Number(existingImmo.amount));
        }
        setImmoExpanded(true);
      } else {
        setImmoDuration(0);
        setImmoAmountOverride(null);
        setImmoNote('');
        setImmoExpanded(false);
      }

      setInitialized(true);
    }
  }, [open, operation, initialized]);

  // Recalculer les montants quand HT ou TVA change
  // PM Spec 17/02/2026 — l'immobilisation s'AJOUTE au HT, TVA s'applique dessus (option 4a)
  useEffect(() => {
    if (!initialized) return;
    const htWithImmo = Number(montantHT || 0) + Number(immoFinalAmount || 0);
    if (applyTVA) {
      const tva = Math.round(htWithImmo * (tauxTVA / 100));
      setMontantTVA(tva);
      setInvoiceAmount(htWithImmo + tva);
    } else {
      setMontantTVA(0);
      setInvoiceAmount(htWithImmo);
    }
  }, [montantHT, tauxTVA, applyTVA, initialized, immoFinalAmount]);

  const handleSubmit = async () => {
    if (isCustom && !customPaymentText.trim()) {
      toast({ title: 'Champ requis', description: 'Veuillez saisir les conditions négociées.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      // PM Spec 17/02/2026 — bloc immobilisation envoyé seulement si applicable
      const immobilization = (immoConfig && Number(immoDuration) > 0 && immoFinalAmount > 0) ? {
        duration: Number(immoDuration),
        unit: immoConfig.unit,
        rate: immoConfig.rate,
        franchise: immoConfig.franchise,
        amount: immoAmountOverride !== null ? Number(immoAmountOverride) : null, // null = laisse backend calculer,
        note: immoNote.trim() || null,
      } : null;
      const response = await axios.post(
        `${API}/financial/${operation.id}/generate-invoice`,
        {
          amount: invoiceAmount,
          montantHT,
          montantTVA: applyTVA ? montantTVA : 0,
          tauxTVA: applyTVA ? tauxTVA : 0,
          applyTVA,
          notes: invoiceNotes,
          payment_policy_id: selectedPolicyId || null,
          custom_payment_text: isCustom ? customPaymentText : null,
          // Immobilisation : null si non applicable
          immobilization,
          immobilizationIncludedInHT: false, // montantHT est le HT TRANSPORT, backend ajoute immo
        },
        {}
      );

      if (response.data.success) {
        const isRegenerated = response.data.action === 'INVOICE_REGENERATED';
        toast({
          title: isRegenerated ? "✅ Facture régénérée" : "✅ Facture générée",
          description: `Facture ${response.data.invoice_number} ${isRegenerated ? 'régénérée' : 'créée'} avec succès.`
        });

        // Télécharger la facture si le PDF a été généré
        if (response.data.pdf_generated) {
          try {
            const downloadResponse = await axios.get(
              `${API}/documents/download/${response.data.invoice_number}`,
              { responseType: 'blob'}
            );
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Facture_${response.data.invoice_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          } catch (dlError) {
            console.warn('Download failed:', dlError);
          }
        }

        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de régénérer la facture",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!operation) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-amber-600" />
            Régénérer la facture
          </DialogTitle>
          <DialogDescription>
            Opération: <span className="font-medium">{operation.operationNumber || operation.id}</span>
            {operation.invoiceNumber && (
              <span className="ml-2 text-blue-600">• Facture actuelle: {operation.invoiceNumber}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* ✨ Alerte : tarif modifié depuis la dernière facture */}
          {pricingDrift && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50"
              data-testid="pricing-drift-banner"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">
                  Le tarif client a été modifié depuis la dernière facture
                </div>
                <div className="text-amber-800 mt-0.5">
                  Ancien montant facturé :{' '}
                  <span className="line-through">{pricingDrift.from.toLocaleString('fr-FR')} XOF</span>
                  {' → '}
                  <strong>{pricingDrift.to.toLocaleString('fr-FR')} XOF</strong>
                </div>
                <div className="text-xs text-amber-700 mt-1">
                  Le nouveau montant a été pré-rempli. Régénérez la facture pour appliquer la modification.
                </div>
              </div>
            </div>
          )}

          {/* Résumé */}
          <Card className="bg-gray-50">
            <CardContent className="pt-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">Trajet:</span>
                <span className="font-medium text-right max-w-[70%]">{(() => {
                  const shorten = (a) => {
                    if (!a) return '?';
                    const clean = a.replace(/,?\s*Côte d'Ivoire$/i, '');
                    const parts = clean.split(',').map(p => p.trim()).filter(Boolean);
                    const meaningful = parts.filter(p => p.toLowerCase() !== 'abidjan');
                    return meaningful[0] || parts[0] || a;
                  };
                  const stops = operation.stops || operation.etapes || [];
                  const parts = [shorten(operation.departure), ...stops.map(s => shorten(s.address || s.name || '')), shorten(operation.arrival)];
                  return parts.join(' → ');
                })()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Client:</span>
                <span className="font-medium">
                  {operation.companyName || `${operation.firstName || ''} ${operation.lastName || ''}`.trim() || 'Non spécifié'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Montants */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="montantHT">Montant HT (XOF)</Label>
              <Input id="montantHT" type="number" value={montantHT}
                onChange={(e) => setMontantHT(Number(e.target.value))} className="text-right font-mono" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch id="applyTVA" checked={applyTVA} onCheckedChange={setApplyTVA} />
                <Label htmlFor="applyTVA" className={applyTVA ? "text-gray-900" : "text-gray-500"}>
                  {applyTVA ? `TVA (${tauxTVA}%)` : "Sans TVA"}
                </Label>
              </div>
              {applyTVA && (
                <Input type="number" value={tauxTVA} onChange={(e) => setTauxTVA(Number(e.target.value))}
                  className="w-20 text-right font-mono" min={0} max={100} />
              )}
            </div>
            {applyTVA && (
              <div className="flex justify-between text-sm text-gray-600 pl-2">
                <span>TVA:</span>
                <span className="font-mono">{montantTVA.toLocaleString('fr-FR')} XOF</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between items-center bg-green-50 p-3 rounded-lg">
              <span className="font-semibold text-green-800">Montant TTC:</span>
              <span className="text-xl font-bold text-green-700 font-mono">
                {invoiceAmount.toLocaleString('fr-FR')} XOF
              </span>
            </div>
          </div>

          {/* ── PM Spec 17/02/2026 — Frais d'immobilisation ── */}
          {immoConfig && (
            <>
              <Separator />
              <div className="space-y-2" data-testid="immo-section">
                <button
                  type="button"
                  onClick={() => setImmoExpanded(!immoExpanded)}
                  className="w-full flex items-center justify-between gap-2 text-left p-2 rounded-lg hover:bg-amber-50 transition"
                  data-testid="immo-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-800">
                      Frais d'immobilisation (du devis)
                    </span>
                    {immoFinalAmount > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-mono">
                        +{immoFinalAmount.toLocaleString('fr-FR')} XOF
                      </Badge>
                    )}
                  </div>
                  {immoExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {immoExpanded && (
                  <Card className="bg-amber-50/40 border-amber-200">
                    <CardContent className="pt-4 space-y-3 text-sm">
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Tarif convenu :</span>
                        <span className="font-mono">{immoConfig.rate.toLocaleString('fr-FR')} XOF / jour</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Franchise :</span>
                        <span className="font-mono">{immoConfig.franchise} {immoConfig.unit}</span>
                      </div>

                      <div className="space-y-1 pt-1">
                        <Label htmlFor="immo-duration" className="text-xs">
                          Durée effective d'immobilisation ({immoConfig.unit})
                        </Label>
                        <Input
                          id="immo-duration"
                          data-testid="immo-duration-input"
                          type="number"
                          min={0}
                          value={immoDuration}
                          onChange={(e) => { setImmoDuration(e.target.value); setImmoAmountOverride(null); }}
                          className="text-right font-mono"
                          placeholder="Ex: 36"
                        />
                      </div>

                      {Number(immoDuration) > 0 && (
                        <div className="text-xs text-slate-700 bg-white rounded p-2 space-y-1 border border-amber-200">
                          <div className="flex justify-between">
                            <span>Quantité facturable :</span>
                            <span className="font-mono">
                              max(0, {Number(immoDuration)} − {immoConfig.franchise}) = <strong>{immoBillable} {immoConfig.unit}</strong>
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Montant calculé :</span>
                            <span className="font-mono font-semibold text-amber-700">
                              {immoAutoAmount.toLocaleString('fr-FR')} XOF
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <Switch
                          id="immo-override"
                          checked={immoAmountOverride !== null}
                          onCheckedChange={(checked) => {
                            setImmoAmountOverride(checked ? immoAutoAmount : null);
                          }}
                          data-testid="immo-override-toggle"
                        />
                        <Label htmlFor="immo-override" className="text-xs cursor-pointer">
                          Ajuster manuellement le montant
                        </Label>
                      </div>

                      {immoAmountOverride !== null && (
                        <div className="space-y-1">
                          <Input
                            type="number"
                            min={0}
                            value={immoAmountOverride}
                            onChange={(e) => setImmoAmountOverride(Number(e.target.value))}
                            className="text-right font-mono"
                            data-testid="immo-amount-override-input"
                          />
                          <p className="text-[10px] text-amber-700">
                            ⚠ Montant ajusté — un badge "ajusté" apparaîtra sur la facture pour traçabilité.
                          </p>
                        </div>
                      )}

                      <div className="space-y-1 pt-1">
                        <Label htmlFor="immo-note" className="text-xs">
                          Note sur la ligne (optionnel)
                        </Label>
                        <Textarea
                          id="immo-note"
                          data-testid="immo-note-input"
                          value={immoNote}
                          onChange={(e) => setImmoNote(e.target.value)}
                          rows={2}
                          placeholder="Ex: Immobilisation chantier Bouaké du 12 au 15/02"
                          className="text-xs"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Modalités de paiement */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <CreditCard className="w-4 h-4" /> Modalités de paiement
            </Label>
            <select
              data-testid="regen-payment-policy-select"
              value={selectedPolicyId}
              onChange={(e) => { setSelectedPolicyId(e.target.value); setCustomPaymentText(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
            >
              <option value="">-- Choisir une modalité --</option>
              {policies.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {selectedPolicy && !isCustom && (
              <p className="text-xs text-gray-500 bg-gray-50 rounded p-2 leading-relaxed">
                {selectedPolicy.description || selectedPolicy.payment_text}
              </p>
            )}
            {isCustom && (
              <Textarea
                value={customPaymentText}
                onChange={(e) => setCustomPaymentText(e.target.value)}
                placeholder="Ex: 60% avant mise à disposition, 40% à livraison..."
                rows={2}
                className="text-sm"
              />
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label htmlFor="invoiceNotes">Notes / Observations (optionnel)</Label>
            <Textarea id="invoiceNotes" value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)}
              placeholder="Notes additionnelles..." rows={2} />
          </div>

          {operation.invoiceNumber && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">Un nouveau numéro de facture sera généré automatiquement.</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={loading || invoiceAmount <= 0}
            className="bg-amber-600 hover:bg-amber-700">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</> :
              <><FileText className="w-4 h-4 mr-2" />Régénérer la facture</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegenerateInvoiceModal;
